import { ref } from 'vue'
import { useSettingsStore } from '@/store/settings'
import { useCartStore } from '@/store/cart'
import { useNotesStore } from '@/store/notes'
import { usePlausible } from '@/composables/usePlausible'

type PageSize = 'A0' | 'A2' | 'A3' | 'A4' | 'Letter'
type Orientation = 'portrait' | 'landscape'

interface LineOverviewExportOptions {
  size: PageSize
  orientation: Orientation
  filters: Record<string, string[] | undefined> | { styles?: (string | number)[] }
  mode?: string
}

interface WorkbookExportOptions {
  categories?: string[]
  useFilters?: boolean
  filters?: Record<string, string[]>
  all?: boolean
  styles?: (string | number)[]
  cart?: boolean
  cartdata?: { saved: (string | number)[], items: Record<string, { qty: number }> }
  notesdata?: Record<string | number, string>
  mode?: string
  singleProduct?: boolean
}

const WORKBOOK_SIZE: PageSize = 'A2'
const WORKBOOK_ORIENTATION: Orientation = 'portrait'
const PDF_EXPORT_TIMEOUT_MS = 70000

function getDateString() {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export function usePdfExport() {
  const isExporting = useState<boolean>('pdfExporting', () => false)
  const toast = useToast()
  const settings = useSettingsStore()
  const cartStore = useCartStore()
  const notesStore = useNotesStore()
  const { trackEvent } = usePlausible()

  function buildFilterParams(filters: Record<string, string[]> | undefined | { styles?: (string | number)[] }) {
    const params = new URLSearchParams()
    if (!filters) return params
    for (const [k, arr] of Object.entries(filters)) {
      if (k === 'styles' && Array.isArray(arr)) {
        params.set('styles', arr.join(','))
      } else if (Array.isArray(arr)) {
        for (const v of arr) params.append(k, String(v))
      }
    }
    return params
  }

  // ALWAYS use POST
  async function exportLineOverview(opts: LineOverviewExportOptions) {
    if (isExporting.value) return
    isExporting.value = true
    toast.add({ title: 'Export started', description: 'Generating Line Overview PDF…', icon: 'i-lucide-printer' })
    
    // Track PDF export event
    trackEvent('Export PDF', { 
      exportType: 'line-overview',
      pageSize: opts.size,
      orientation: opts.orientation
    })
    
    try {
      const body: Record<string, any> = {
        size: opts.size,
        orientation: opts.orientation,
        view: 'overview',
        currency: settings.currency,
        ...opts.filters
      }
      if (opts.mode) {
        body.mode = opts.mode
      }
      const res = await fetchWithTimeout('/api/pdf/products', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      }, PDF_EXPORT_TIMEOUT_MS)
      if (!res.ok) throw new Error(await res.text().catch(() => 'Server error'))
      const blob = await res.blob()
      triggerDownload(blob, `line-overview-${getDateString()}.pdf`)
      toast.add({ title: 'Export ready', description: 'Download started', icon: 'i-lucide-file-down' })
    } catch (e: any) {
      const message = e?.name === 'AbortError'
        ? 'PDF generation timed out. Please try again.'
        : e?.message || 'Unknown error'
      toast.add({ title: 'Export failed', description: message, icon: 'i-lucide-triangle-alert' })
    } finally {
      isExporting.value = false
    }
  }

  async function exportWorkbook(opts: WorkbookExportOptions) {
    if (isExporting.value) return
    isExporting.value = true
    toast.add({ title: 'Export started', description: `Generating Workbook PDF…`, icon: 'i-lucide-printer' })
    
    // Track PDF export event
    trackEvent('Export PDF', { 
      exportType: 'workbook',
      hasCart: opts.cart ? 'yes' : 'no',
      stylesCount: opts.styles?.length || 0
    })
    
    try {
      let cartdata: string | undefined
      if (opts.cart) {
        // Använd cartdata från opts om finns, annars från cartStore
        const cartPayload = opts.cartdata 
          ? opts.cartdata 
          : {
              saved: Array.from(cartStore.savedStyles),
              items: Object.fromEntries(
                Object.entries(cartStore.lineItems).map(([sku, li]) => [sku, { qty: li.qty }])
              )
            }
        cartdata = btoa(JSON.stringify(cartPayload))
      }
      
      // Include notes data
      const notesPayload = opts.notesdata || notesStore.notes
      const notesdata = btoa(JSON.stringify({ v: 1, notes: notesPayload }))
      
      const body: Record<string, any> = {
        view: 'workbook',
        size: WORKBOOK_SIZE,
        orientation: WORKBOOK_ORIENTATION,
        currency: settings.currency,
      }
      if (opts.mode) {
        body.mode = opts.mode
      }
      if (opts.styles && opts.styles.length) {
        body.styles = opts.styles
      }
      if (opts.cart && cartdata) {
        body.cart = '1'
        body.cartdata = cartdata
      }
      if (notesdata) {
        body.notesdata = notesdata
      }
      if (opts.categories?.length) {
        body.categories = opts.categories.join(',')
      }
      if (opts.useFilters && opts.filters) {
        for (const [k, arr] of Object.entries(opts.filters)) {
          body[k] = arr
        }
        body.filtered = '1'
      }
      if (opts.all) {
        body.all = true
      }
      if (opts.singleProduct) {
        body.singleProduct = '1'
      }

      const res = await fetchWithTimeout('/api/pdf/products', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      }, PDF_EXPORT_TIMEOUT_MS)

      if (!res.ok) throw new Error(await res.text().catch(() => 'Server error'))
      const blob = await res.blob()
      triggerDownload(blob, `workbook-${getDateString()}.pdf`)
      toast.add({ title: 'Export ready', description: 'Download started', icon: 'i-lucide-file-down' })
    } catch (e: any) {
      const message = e?.name === 'AbortError'
        ? 'PDF generation timed out. Please try again.'
        : e?.message || 'Unknown error'
      toast.add({ title: 'Export failed', description: message, icon: 'i-lucide-triangle-alert' })
    } finally {
      isExporting.value = false
    }
  }

  // Alias for consistency with new code/PR
  const exportWorkbookPdf = exportWorkbook

  function triggerDownload(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  return {
    isExporting,
    exportLineOverview,
    exportWorkbook,
    exportWorkbookPdf
  }
}

async function fetchWithTimeout(input: RequestInfo | URL, init: RequestInit, timeoutMs: number) {
  const controller = new AbortController()
  const id = setTimeout(() => controller.abort(), timeoutMs)
  try {
    const res = await fetch(input, { ...init, signal: controller.signal })
    return res
  } finally {
    clearTimeout(id)
  }
}
